/** T84 — Doctor weekly-schedule + leave + availability client. */
import { apiClient } from "./client";

export interface ScheduleBand {
  /** null when the row is a draft (FE-only) — server assigns on PUT. */
  id: string | null;
  /** 1 = Monday … 7 = Sunday (ISO). */
  dayOfWeek: number;
  /** "HH:mm" or "HH:mm:ss". */
  startTime: string;
  endTime: string;
  slotMinutes: number;
}

export interface LeaveDay {
  id: string;
  leaveDate: string; // YYYY-MM-DD
  reason: string | null;
}

export interface AvailabilitySlot {
  startTime: string; // HH:mm:ss
  endTime: string;
  available: boolean;
  reason: string | null;
  /**
   * #19954 — false: khung của Buổi khám nằm ngoài giờ trực của bác sĩ đang xem ("BS không trực").
   * Bấm vào thì bỏ chọn bác sĩ và đặt lịch không bác sĩ. Vắng trên API cũ = true.
   */
  doctorOnDuty?: boolean;
}

export interface AvailabilityResponse {
  doctorProfileId: string;
  date: string;
  dayOfWeek: number;
  onLeave: boolean;
  leaveReason: string | null;
  slots: AvailabilitySlot[];
  /** #19939 — lý do cả ngày không đặt được (đúng câu bước gửi sẽ trả); vắng/null = ngày mở. */
  closedCode?: string | null;
  closedMessage?: string | null;
}

export const DAY_LABELS: { value: number; label: string; short: string }[] = [
  { value: 1, label: "Thứ Hai", short: "T2" },
  { value: 2, label: "Thứ Ba", short: "T3" },
  { value: 3, label: "Thứ Tư", short: "T4" },
  { value: 4, label: "Thứ Năm", short: "T5" },
  { value: 5, label: "Thứ Sáu", short: "T6" },
  { value: 6, label: "Thứ Bảy", short: "T7" },
  { value: 7, label: "Chủ Nhật", short: "CN" },
];

export const SLOT_OPTIONS = [10, 15, 20, 30, 45, 60];

/** "08:00" → "08:00:00" — server accepts both, FE inputs return the short form. */
export function normaliseTime(hhmm: string): string {
  return hhmm.length === 5 ? `${hhmm}:00` : hhmm;
}

/** "08:00:00" → "08:00" for `<input type="time">` value attribute. */
export function timeForInput(serverTime: string): string {
  return serverTime.slice(0, 5);
}

export const schedulesApi = {
  getSchedule(doctorId: string): Promise<ScheduleBand[]> {
    return apiClient
      .get<ScheduleBand[]>(`/v1/doctors/${doctorId}/schedule`)
      .then((r) => r.data);
  },
  replaceSchedule(
    doctorId: string,
    bands: ScheduleBand[]
  ): Promise<ScheduleBand[]> {
    return apiClient
      .put<ScheduleBand[]>(`/v1/doctors/${doctorId}/schedule`, {
        bands: bands.map((b) => ({
          dayOfWeek: b.dayOfWeek,
          startTime: normaliseTime(b.startTime),
          endTime: normaliseTime(b.endTime),
          slotMinutes: b.slotMinutes,
        })),
      })
      .then((r) => r.data);
  },
  listLeaves(
    doctorId: string,
    from?: string,
    to?: string
  ): Promise<LeaveDay[]> {
    return apiClient
      .get<LeaveDay[]>(`/v1/doctors/${doctorId}/leaves`, {
        params: { from, to },
      })
      .then((r) => r.data);
  },
  addLeave(
    doctorId: string,
    leaveDate: string,
    reason: string | null
  ): Promise<LeaveDay> {
    return apiClient
      .post<LeaveDay>(`/v1/doctors/${doctorId}/leaves`, {
        leaveDate,
        reason: reason || null,
      })
      .then((r) => r.data);
  },
  removeLeave(doctorId: string, leaveId: string): Promise<void> {
    return apiClient
      .delete(`/v1/doctors/${doctorId}/leaves/${leaveId}`)
      .then(() => undefined);
  },
  /** `roomId` (#19943): phòng đang chọn — khung phòng đã đủ chỗ cũng trả về "Đã đặt". */
  availability(doctorId: string, date: string, roomId?: string | null): Promise<AvailabilityResponse> {
    const params: Record<string, string> = { date };
    if (roomId) params.roomId = roomId;
    return apiClient
      .get<AvailabilityResponse>(`/v1/doctors/${doctorId}/availability`, { params })
      .then((r) => r.data);
  },
};

export const schedulesQueryKeys = {
  schedule: (doctorId: string) => ["doctor-schedule", doctorId] as const,
  leaves: (doctorId: string) => ["doctor-leaves", doctorId] as const,
  availability: (doctorId: string, date: string, roomId?: string | null) =>
    ["doctor-availability", doctorId, date, roomId ?? null] as const,
};
